import { useCallback, useMemo, useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  QDropdownWrapper,
  QButton,
  QTypography,
  QContainer,
  QTable,
  QTableHead,
  QTableRow,
  QTableBody,
  QTableCell,
  QTableSortableColumnHeader,
  QSearchBar,
  QAsyncTablePagination,
} from '@kw/quokka-ui';

import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { Policy } from '@kw/rules-service/dist/src/policy/models/policy.model';
import { useQPagination } from '../../kw-ui-components/Pagination/useQPagination';

import { OrganizationDropdown } from './OrganizationDropdown';
import { useFetchService } from '../../utils/fetchService';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { sendHttpRequest } from '../../utils/network.service';
import { usePoliciesSortParams } from '../Policy/usePoliciesSortParams';
import { getComparator } from '../../utils/getComparator';

const DEFAULT_POLICIES = [
  {
    policyName: 'Malicious Apps',
    policyDescription:
      "Apps that trigger Quokka's Malware Detection engine, or have a contained file that matches a known malware signature.",
    ruleName: 'Malware',
  },
  {
    policyName: 'Overprivileged Apps',
    policyDescription: 'Apps that ask for more permissions than they programmatically use.',
    ruleName: 'Requests more permissions than it uses',
  },
  {
    policyName: 'Leaky Apps',
    policyDescription: 'Apps that leak or expose personally identifiable information (PII) or other important user data.',
    ruleName: 'Leaks critical user data, password or other information',
  },
  {
    policyName: 'Chatty Apps',
    policyDescription:
      'Apps that interact with the messaging or phone functionality on devices, giving them the ability to communicate externally.',
    ruleName: 'App with communication capabilities',
  },
  {
    policyName: 'Zombie Apps',
    policyDescription: 'Apps that remain persistent in memory after they have been closed, causing issues like high battery drain.',
    ruleName: 'Memory persistence',
  },
  {
    policyName: 'Harvester Apps',
    policyDescription:
      'Apps potentially providing a popular or important function which harvest user data by accessing sensitive permissions and sharing that data with Ad/Social Networks and/or risky locations.',
    ruleName: 'App requests permissions and shares data',
  },
  {
    policyName: 'Sloppy Apps',
    policyDescription:
      'Apps that do not use industry standard data protection mechanisms, violating OWASP M7 mobile risks for client code quality.',
    ruleName: 'App with poor code standards',
  },
];

interface OrgPolicy extends Policy {
  id: string;
}

export const Policies = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedPolicy, setSelectedPolicy] = useState<string>('');
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [searchQuery, setSearchQuery] = useState('');

  const { policyNameOrder, togglePolicyNameOrder, orderBy, setOrderBy } = usePoliciesSortParams();

  const { page, pageSize, handleChangeRowsPerPage, setPage } = useQPagination(10);

  const { data: rulesResp = { results: [] } } = useFetchService(`/eval/rules/list`, accessToken);
  const { data: policiesData, error: policiesError } = useFetchService(`/eval/policies/list/${selectedOrganization}`, accessToken);

  const filteredPolicies = useMemo(() => {
    const policies = policiesData ? policiesData.results : [];
    return searchQuery.length
      ? policies.filter(
          (item: OrgPolicy) =>
            item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.description?.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : policies;
  }, [policiesData, searchQuery]);

  const total = filteredPolicies.length;
  const totalPageCount = Math.ceil(total / pageSize);

  const tableHeaders = [
    {
      title: 'Policy Name',
      onClick: togglePolicyNameOrder,
      sortValue: policyNameOrder,
    },
    {
      title: 'Policy Description',
      onClick: () => {},
      sortValue: null,
    },
    {
      title: 'Created Date',
      onClick: () => {},
      sortValue: null,
    },
    {
      title: 'Last Modified Date',
      onClick: () => {},
      sortValue: null,
    },
    {
      title: 'Enabled',
      onClick: () => {},
      sortValue: null,
    },
  ];

  const order = tableHeaders.map(({ sortValue }) => sortValue).find(o => o);

  useEffect(() => {
    if (policyNameOrder) {
      setOrderBy('name');
    }
  }, [policyNameOrder, order, orderBy, setOrderBy]);

  const rulesByName = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const r of rulesResp.results ?? []) map.set(r.name, r);
    return map;
  }, [rulesResp.results]);

  const policyOptions = useMemo(
    () =>
      DEFAULT_POLICIES.map(p => ({
        label: `${p.policyName} — ${p.ruleName}`,
        value: p.policyName,
      })),
    [],
  );

  const canCreateOne = Boolean(selectedOrganization && selectedPolicy);
  const canCreateAll = Boolean(selectedOrganization);

  const createPolicy = useCallback(
    async ({ policyName, policyDescription, ruleId }: { policyName: string; policyDescription: string; ruleId: string }) => {
      if (!selectedOrganization) {
        enqueueSnackbar('Please select an organization first.', { variant: 'warning' });
        return;
      }
      if (!ruleId) {
        enqueueSnackbar(`No matching rule found for "${policyName}".`, { variant: 'error' });
        return;
      }

      await sendHttpRequest({
        path: `${process.env.EVAL_SERVICE_URL}/eval/policies/create/global`,
        method: 'POST',
        body: {
          orgId: selectedOrganization,
          name: policyName,
          description: policyDescription,
          enabled: false,
          policyRules: [
            {
              ruleId,
            },
          ],
        },
        successMessage: `Policy "${policyName}" created successfully`,
        errorMessage: `Error creating policy "${policyName}"`,
        enqueueSnackbar,
      });
    },
    [enqueueSnackbar, selectedOrganization],
  );

  const createSinglePolicy = useCallback(
    async (policyName: string) => {
      const matched = DEFAULT_POLICIES.find(p => p.policyName === policyName);
      if (!matched) {
        enqueueSnackbar(`Unknown policy "${policyName}".`, { variant: 'error' });
        return;
      }
      const rule = rulesByName.get(matched.ruleName);
      await createPolicy({
        policyName: matched.policyName,
        policyDescription: matched.policyDescription,
        ruleId: rule?.id ?? '',
      });
    },
    [createPolicy, enqueueSnackbar, rulesByName],
  );

  const createAllPolicies = useCallback(async () => {
    const tasks = DEFAULT_POLICIES.map(p => {
      const rule = rulesByName.get(p.ruleName);
      return createPolicy({
        policyName: p.policyName,
        policyDescription: p.policyDescription,
        ruleId: rule?.id ?? '',
      });
    });

    const results = await Promise.allSettled(tasks);
    const ok = results.filter(r => r.status === 'fulfilled').length;
    const fail = results.length - ok;

    enqueueSnackbar(`Created ${ok} policies${fail ? `, ${fail} failed` : ''}.`, {
      variant: fail ? 'warning' : 'success',
    });
  }, [createPolicy, enqueueSnackbar, rulesByName]);

  return (
    <>
      <OrganizationDropdown setSelectedOrganization={setSelectedOrganization} />

      <Box p={2}>
        <QTypography variant="h4Bold">Default Policy</QTypography>

        <QDropdownWrapper
          selectedOption={selectedPolicy}
          closedLabel={policyOptions.find(o => o.value === selectedPolicy)?.label ?? 'Select Policy'}
          formControlProps={{ sx: { width: '100%' } }}
          onChange={e => setSelectedPolicy(e.target.value as string)}
          options={policyOptions}
        />
      </Box>

      <Box p={2}>
        <QButton disabled={!canCreateOne} variant="filled" onClick={() => createSinglePolicy(selectedPolicy)}>
          Create Policy
        </QButton>

        <QButton sx={{ ml: 1 }} variant="outlined" disabled={!canCreateAll} onClick={createAllPolicies}>
          Create All Policies
        </QButton>
      </Box>
      {selectedOrganization ? (
        <>
          {policiesError ? (
            <QContainer maxWidth="false">
              <div>failed to load</div>
            </QContainer>
          ) : (
            <QContainer maxWidth="false">
              <StyledTableWrapper>
                <QSearchBar
                  searchValue={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search organization policies"
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      setSearchQuery(e.target.value.toString());
                      setPage(0);
                    }
                  }}
                  onRemove={() => {
                    setSearchQuery('');
                    setPage(0);
                  }}
                />
                {total > 10 ? (
                  <QAsyncTablePagination
                    page={page + 1}
                    pagination={{
                      dataPerPage: pageSize,
                      totalData: total,
                      lastPage: totalPageCount,
                    }}
                    getPage={newPage => setPage(newPage - 1)}
                    showRowsPerPage={true}
                    currentRows={(page + 1) * pageSize > filteredPolicies.length ? filteredPolicies.length % pageSize : pageSize}
                    handlePageSizeChange={handleChangeRowsPerPage}
                    pageSize={pageSize}
                    pageSizeOptions={[10, 20, 50, 100].map(v => ({ label: v, value: v }))}
                  />
                ) : null}
                <QTable data-testid="customer-support-policies-table">
                  <QTableHead>
                    <QTableRow>
                      {tableHeaders.map(({ title, onClick, sortValue }) => (
                        <QTableSortableColumnHeader
                          key={title}
                          title={title}
                          onClick={() => {
                            onClick();
                          }}
                          sortValue={sortValue}
                        />
                      ))}
                    </QTableRow>
                  </QTableHead>
                  <QTableBody>
                    {[...filteredPolicies]
                      .sort(getComparator(order, orderBy))
                      .slice(page * pageSize, (page + 1) * pageSize)
                      .map(policy => {
                        const policyCreatedAt = dayjs(policy.createdAt).format('MM/DD/YYYY');
                        const policyUpdatedAt = dayjs(policy.updatedAt).format('MM/DD/YYYY');
                        return (
                          <QTableRow role="row" aria-label={`${policy.name} row`} key={policy.id}>
                            <QTableCell>{policy.name}</QTableCell>
                            <QTableCell>{policy.description}</QTableCell>
                            <QTableCell>{policyCreatedAt}</QTableCell>
                            <QTableCell>{policyUpdatedAt}</QTableCell>
                            <QTableCell>{policy.enabled ? 'Yes' : 'No'}</QTableCell>
                          </QTableRow>
                        );
                      })}
                  </QTableBody>
                </QTable>
              </StyledTableWrapper>
            </QContainer>
          )}
        </>
      ) : null}
    </>
  );
};

const StyledTableWrapper = styled('div')`
  width: 100%;
`;
